import { Button, Select, SelectItem } from "@nextui-org/react";
import React, { useEffect, useState } from "react";
import { ProgessColors } from "../../utils/constant";
import { CreateReportFieldSchema } from "../../constant/Report/reportConstant";
import { TextAreaInput, TextInput } from "../FormBuilder/InputField/InputField.jsx";
import { getDataWithFilterUrl } from "../../controllers/APIController";

/**
 * FirstReportPart Component
 * First step of the report creation wizard
 * Collects basic report information and framework selection
 * @param {Object} props - Component props
 * @param {Object} props.DataList - Current report data
 * @param {Function} props.setDataList - Function to update report data
 * @param {number} props.currentPage - Current wizard page number
 * @param {Function} props.onSubmitHandler - Function to handle form submission
 * @returns {JSX.Element} First step of report creation wizard
 */
const FirstReportPart = ({
  DataList,
  setDataList,
  currentPage,
  onSubmitHandler,
}) => {
  // Schema data for form fields
  const schemaData = CreateReportFieldSchema;

  // Component state
  const [validationErrors, setValidationErrors] = useState({});
  const [orgFrameworkDetails, setOrgFrameworkDetails] = useState([]);
  
  // User and organization data from session storage
  const userLogged = JSON.parse(sessionStorage.getItem("userData"));
  const orgInfo = userLogged && userLogged.user.LastOrgId;

  // Selected framework for validation
  const selectedFramework = DataList?.frameworkId || null;

  /**
   * Validate form data before submission
   * Checks required fields and sets validation errors
   * @returns {boolean} Whether the form is valid
   */
  const validateForm = () => {
    const errors = {};
    let isValid = true;
    setValidationErrors({});

    const reportName = DataList?.Name;
    const currentFrameworkId = DataList?.frameworkId;
    
    if (!reportName) {
      errors["Name"] = "Report Name is required!";
      isValid = false;
    }

    if (!currentFrameworkId) {
      errors["frameworkId"] = "Framework is required!";
      isValid = false;
    }

    setValidationErrors(errors);
    return isValid;
  };

  /**
   * Handle next step or form submission
   * Validates form and calls submission handler if valid
   */
  const handleNext = () => {
    if (!validateForm()) {
      return;
    }
    onSubmitHandler();
  };

  /**
   * Fetch organization frameworks on component mount
   * Loads available frameworks for the current organization
   */
  useEffect(() => {
    const filterUrl = "filters[organizations][id][$eqi]=" + orgInfo;
    
    getDataWithFilterUrl("frameworks", filterUrl)
      .then((data) => {
        const response = data?.data;

        const frameworkList = response?.map((framework) => ({
          key: framework?.attributes?.Name,
          name: framework?.attributes?.Name,
        }));

        setOrgFrameworkDetails(frameworkList || []);
      })
      .catch((error) => {
        console.error("Error fetching frameworks:", error);
      });
  }, [orgInfo]);

  return (
    <div>
      <div className="min-h-[500px]">
        {/* Form Header */}
        <div className="flex flex-col gap-2">
          <p className="text-2xl font-medium">Basic Information</p>
          <p>
            Please provide the basic details required to create your report.
            Ensure that all necessary fields are filled out accurately to avoid
            any issues during the report generation process.
          </p>
        </div>
        
        {/* Form Fields */}
        <div className="flex flex-col gap-2 my-4">
          {/* Report Name Input */}
          <TextInput
            data={schemaData["Name"]}
            value={DataList["Name"]}
            validationErrors={validationErrors}
            onChangeHandler={(title, value, type) => {
              setDataList({ ...DataList, [title]: value });
            }}
            cssStyle="my-2"
          />
          
          {/* Report Description Input */}
          <TextAreaInput
            data={schemaData["Description"]}
            value={DataList["Description"]}
            validationErrors={validationErrors}
            onChangeHandler={(title, value, type) => {
              setDataList({ ...DataList, [title]: value });
            }}
            cssStyle="my-4"
          />
          
          {/* Framework Selection */}
          <Select
            isRequired
            label="Select Framework"
            placeholder="Select any Framework"
            variant="faded"
            selectionMode="single"
            labelPlacement="outside"
            className="my-8"
            selectedKeys={selectedFramework && new Set([selectedFramework])}
            onSelectionChange={(event) => {
              setDataList({ ...DataList, frameworkId: Array.from(event)[0] });
            }}
            errorMessage={validationErrors["frameworkId"]}
            isInvalid={validationErrors["frameworkId"]}
          >
            {orgFrameworkDetails?.map((framework) => (
              <SelectItem 
                key={framework.key} 
                value={framework.name} 
                className="py-2"
              >
                {framework.name}
              </SelectItem>
            ))}
          </Select>
        </div>
      </div>
      
      {/* Action Buttons */}
      <div className="w-full flex items-center my-8 gap-4 justify-end">
        <Button
          color={ProgessColors[currentPage]}
          className="text-center cursor-pointer"
          onClick={handleNext}
        >
          Generate
        </Button>
      </div>
    </div>
  );
};

export default FirstReportPart;
